import { OrderClient, OrderWorkflowClient } from '@frontend/order/api';
import { ExtendedOrder, UpdateOrder, UpdateOrderState } from '@frontend/order/types';
import { useAccountRepository } from '@frontend/repository';
import { ToastUtil } from '@frontend/toast-utils';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ViewProps {
    currentAccount: string;
    navigate: ReturnType<typeof useNavigate>;
    detailVersion: number;
    setObject: React.Dispatch<React.SetStateAction<ExtendedOrder | null>>;
    updateWorkflow: UpdateOrder | null;
    setUpdateWorkflow: React.Dispatch<React.SetStateAction<UpdateOrder | null>>;
    onUpdateWorkflow: () => void;
    updateState: UpdateOrderState | null;
    setUpdateState: React.Dispatch<React.SetStateAction<UpdateOrderState | null>>;
    onUpdateState: () => void;
}

export const useOrderDetail = (): ViewProps => {
    const { currentAccount } = useAccountRepository();
    const navigate = useNavigate();
    const [object, setObject] = useState<ExtendedOrder | null>(null);
    const [updateWorkflow, setUpdateWorkflow] = useState<UpdateOrder | null>(null);
    const [updateState, setUpdateState] = useState<UpdateOrderState | null>(null);
    const [detailVersion, setDetailVersion] = useState(0);
    const refreshDetail = () => setDetailVersion((version) => version + 1);

    const onUpdateWorkflow = () => {
        if (updateWorkflow === null) {
            setUpdateWorkflow({ workflow_id: object?.workflow_id });
            return;
        }

        ToastUtil.promise(
            () => OrderClient.patchOrder(object!.account_id, object!.id, updateWorkflow),
            { title: 'Updating Order Workflow' },
            { title: 'Order Workflow Updated', onSuccess: refreshDetail },
            { title: 'Order Workflow Update Failed' }
        );
        setUpdateWorkflow(null);
    };

    const onUpdateState = () => {
        if (updateState === null) {
            setUpdateState({ state: object?.state });
            return;
        }

        ToastUtil.promise(
            () => OrderWorkflowClient.updateOrderState(object!.account_id, object!.id, updateState.state!),
            { title: 'Updating Order State' },
            { title: 'Order State Updated', onSuccess: refreshDetail },
            { title: 'Order State Update Failed' }
        );
        setUpdateState(null);
    };

    return {
        currentAccount,
        navigate,
        detailVersion,
        setObject,
        updateWorkflow,
        setUpdateWorkflow,
        onUpdateWorkflow,
        updateState,
        setUpdateState,
        onUpdateState
    };
};
